import { Component, effect, HostListener, inject, OnDestroy, OnInit, untracked, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";
import { HtmlDialogContentComponent } from "@components/index";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "@progress/kendo-angular-dialog";
import { DropDownFillMode, DropDownRounded, DropDownSize } from "@progress/kendo-angular-dropdowns";
import { IntlService, localeData, setData } from "@progress/kendo-angular-intl";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { CardDisplayService } from "@shared/card-display/card-display.service";
import { CardModalService } from "@shared/card-modal/card-modal.service";
import { IframeMessagesService } from "@shared/iframe-messages-service/iframe-messages.service";
import {
    AnalyticsService,
    AuthService,
    CardService,
    CardViewService,
    ColumnDataLayoutService,
    DashboardsService,
    FiltersService,
    getAnalyticsExtrasFromCard,
    HelpersService,
    iFirmPortalService,
    IfirmTaxCardsService,
    LocalStorageService,
    ResponseErrorHandlerService,
    UsersService,
    UtilsService,
} from "@shared/index";
import { UnassignedUserId } from "@shared/users-service";
import { map, Observable, Subscription } from "rxjs";
import {
    ApplicationType,
    IBaseT1Card,
    IFirmTaxprepT1Card,
    isiFirmTaxDashboard,
    isIFirmTaxprepT1Card,
    isJobsBilling,
    isJobsBillingDashboard,
    isTaxprepT1Card,
    JobsBillingCard,
} from "src/app/views-interfaces";

@Component({
    selector: "card-modal",
    templateUrl: "./card-modal-component.html",
    styleUrl: "./card-modal-component.scss",
    standalone: false,
    host: { class: "wk-cm" },
})
export class CardModalComponent implements OnDestroy, OnInit {
    private readonly intlService = inject(IntlService);
    private readonly authService = inject(AuthService);
    private readonly cardService = inject(CardService);
    private readonly filtersService = inject(FiltersService);
    private readonly cardViewService = inject(CardViewService);
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dialogService = inject(DialogService);
    private readonly ifirmTaxCardsService = inject(IfirmTaxCardsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly iFirmPortalService = inject(iFirmPortalService);
    private readonly cardDisplayService = inject(CardDisplayService);
    public readonly cardModalService = inject(CardModalService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);
    private readonly usersService = inject(UsersService);

    cardsLoadedSubscription: Subscription;
    private routeParamsSubscription: Subscription;
    applicationTypes = ApplicationType;
    cardData: any = {}; // to type
    clientCodeToUse = "";
    cardModalForm;
    showTagsForCard;
    confirmDialogActive = false;
    cardPositionDataInColumn = {
        cardId: null,
        stepId: null,
        cardIds: [],
        currentIndex: 0,
        showLeftArrow: true,
        showRightArrow: true,
    };

    @ViewChild(TooltipDirective) tooltipDir: TooltipDirective;
    isMembersReturnStatusSame = false;
    cardDeleted = false;
    public virtualScrollSettingsForKendoDropDown: any = {
        itemHeight: 32,
        pageSize: 15,
    };

    public AssignedUserDropDownSize: DropDownSize = "small";
    public AssignedUserDropDownRounded: DropDownRounded = "none";
    public AssignedUserDropDownFillMode: DropDownFillMode = "outline";

    private fromType = "";

    constructor() {
        this.cardModalService.reset();
        const columnDataLayoutService = this.columnDataLayoutService;

        this.setWeekStartDay();

        // This code will be called when a card is updated, data.cardId is the card, data.type = added || updated || batch.
        // Data is null if all cards are loaded from cache
        this.cardsLoadedSubscription = columnDataLayoutService.loadCardsFromCacheEvent$.subscribe(async (data) => {
            if (!data && this.columnDataLayoutService.cardsAreLoaded === true) {
                if (this.cardData?.cardId) {
                    const ids = this.cardModalService.members().reduce((acc, member) => {
                        acc.push(member.id);
                        return acc;
                    }, []);
                    this.handleCardChangedBatch({ type: "batch", cardIds: ids });
                } else {
                    await this.loadCardData();
                }
            }
            if (data) {
                if (Array.isArray(data)) {
                    data.filter((d) => d.type).forEach((d) => this.handleCardChangeDetection(d));
                } else if (data.type) {
                    this.handleCardChangeDetection(data);
                }
            }
        });
        this.routeParamsSubscription = this.route.params.subscribe(async () => {
            if (this.authService.loggedIn()) {
                await this.init();
            }
        });

        effect(() => {
            const appType = this.cardModalService.openInApp();
            if (isJobsBilling(appType)) this.handlePendingChanges();
        });

        effect(() => {
            if (this.cardModalService.openContactAndSave()) {
                this.handlePendingChanges();
                this.handleOpenInContact();
                untracked(() => this.cardModalService.openContactAndSave.set(false));
            }
        });

        effect(() => {
            if (this.cardModalService.openContact()) {
                this.handleOpenInContact();
                untracked(() => this.cardModalService.openContact.set(false));
            }
        });

        effect(() => {
            if (this.cardModalService.openPortal()) {
                this.handleOpenInPortal();
                untracked(() => this.cardModalService.openPortal.set(false));
            }
        });

        effect(() => {
            if (this.cardModalService.deleteCard()) {
                this.openDeleteCardConfirmation();
            }
        });

        effect(() => {
            const cardPositionInCol = this.cardModalService.cardPositionInCol();
            if (cardPositionInCol.cardId && cardPositionInCol.stepId) {
                this.handleCardPositionInCol(cardPositionInCol.cardId, cardPositionInCol.stepId);
            }
        });

        effect(() => {
            if (this.cardModalService.closeForm()) {
                this.onClose(null);
            }
        });

        effect(() => {
            const cardId = this.cardModalService.handlePendingChanges();
            if (cardId) {
                this.handlePendingChanges(cardId);
            }
        });
    }

    handleCardChangeDetection(data) {
        switch (data.type) {
            case "updated":
                this.handleCardChangedUpdate(data);
                break;
            case "batch":
                this.handleCardChangedBatch(data);
                break;
            case "delete":
                this.handleCardChangedDelete(data);
                break;
        }
        const index = this.cardModalService.cardIdSaving().indexOf(data.cardId);
        if (index > -1) {
            const updatedCardIdSaving = [...this.cardModalService.cardIdSaving()];
            updatedCardIdSaving.splice(index, 1);
            this.cardModalService.cardIdSaving.set(updatedCardIdSaving);
        }
        return false;
    }

    handleCardChangedUpdate(data) {
        // make sure the updated card isn't the one the user just saved
        const isSelfChange =
            this.cardModalService.cardIdSaving().includes(data.cardId) ||
            (data.card !== undefined && data.card.members?.some((x) => this.cardModalService.cardIdSaving().includes(data.cardId)));
        let currentCard = data.cardId === this.cardModalService.cardId();
        if (currentCard) {
            const newCard = this.cardDisplayService.getCardById(this.cardModalService.cardId());
            if (newCard?._ts == this.cardData._ts) {
                currentCard = undefined;
            }
        }
        const memberChanged = this.checkCardMemberChange(data);
        if (!isSelfChange) {
            // Only update member data if only member changed
            if (!this.cardModalService.showLoader() && !currentCard && memberChanged.length > 0) {
                this.openDialogAfterCurrentCardChange("CardMemberChanged");
            }
            if (!this.cardModalService.showLoader() && currentCard) {
                this.openDialogAfterCurrentCardChange("CardChanged");
            }
        }
    }

    handleCardChangedBatch(data) {
        let currentCard = data.cardIds.includes(this.cardModalService.cardId());
        if (currentCard) {
            const newCard = this.cardDisplayService.getCardById(this.cardModalService.cardId());
            if (newCard?._ts == this.cardData._ts) {
                currentCard = undefined;
            }
        }
        const memberChanged = this.checkCardMemberChange(data);
        if (!this.cardModalService.showLoader() && !currentCard && memberChanged.length > 0) {
            this.openDialogAfterCurrentCardChange("CardMemberChanged");
        }
        if (!this.cardModalService.showLoader() && currentCard) {
            this.openDialogAfterCurrentCardChange("CardChanged");
        }
    }

    openDialogAfterCurrentCardChange(actionType: string): void {
        if (!this.confirmDialogActive) {
            this.confirmDialogActive = true;
            this.openDialogConfirmation({
                title: this.translate.instant("CardModal." + actionType + ".Title"),
                content: this.translate.instant("CardModal." + actionType + ".Content"),
                actions: [
                    { text: this.translate.instant("General.Yes"), primary: true, isYes: true },
                    { text: this.translate.instant("General.No") },
                ],
                width: 600,
                cssClass: "card-pending-changes-dialog",
            }).subscribe(async (result) => {
                this.confirmDialogActive = false;
                if (result) {
                    if (actionType === "CardMemberChanged") {
                        await this.loadCardData();
                    }
                    if (actionType === "CardChanged") {
                        if (this.formChanged()) {
                            this.openPendingChangesDialogConfirmation().subscribe(async (result) => {
                                if (result <= 2) {
                                    if (result === 1) {
                                        this.cardModalService.showLoader.set(true);
                                        await this.saveChanges(false, async () => {
                                            await this.loadCardData();
                                        });
                                    }
                                }
                            });
                        } else {
                            await this.loadCardData();
                        }
                    }
                }
            });
        }
    }

    handleCardChangedDelete(data) {
        const currentCard = data.cardId === this.cardModalService.cardId() || this.cardData.members?.find((x) => x === data.cardId);
        if (currentCard) {
            this.handleCurrentCardDeleted();
        }
        return false;
    }

    handleCurrentCardDeleted() {
        this.cardDeleted = true;
        this.openCurrentCardDeletedDialog().subscribe(async () => {
            this.onClose(null);
        });
    }

    checkCardMemberChange(data) {
        const changedMembers = [];
        switch (data.type) {
            case "updated":
                if (this.checkCardMemberChangeSingle(data.cardId)) {
                    changedMembers.push(data.cardId);
                }
                break;
            case "batch":
                data.cardIds.forEach((cardId) => {
                    if (this.checkCardMemberChangeSingle(cardId)) {
                        changedMembers.push(cardId);
                    }
                });
                break;
        }
        return changedMembers;
    }

    checkCardMemberChangeSingle(cardId) {
        const newCardData = this.cardDisplayService.getCardById(cardId);
        const members = this.cardModalService.cardModel()?.members;
        if (newCardData && members && this.cardData?.members?.find((x) => x.cardId === cardId) && members[cardId]) {
            if (this.cardModalService.memberChangedEvent) return this.cardModalService.memberChangedEvent(newCardData, members[cardId]);
        }
        // If member is no longer found, it changed.
        return newCardData === null || newCardData === undefined;
    }

    openCurrentCardDeletedDialog() {
        return this.openDialogConfirmation({
            title: this.translate.instant("CardDeleted.Title"),
            content: this.translate.instant("CardDeleted.Content"),
            actions: [{ text: "OK", primary: true, isYes: true }],
            width: 600,
            cssClass: "card-pending-changes-dialog",
        });
    }

    openCardNotFoundDialog() {
        return this.openDialogConfirmation({
            title: this.translate.instant("CardNotFound.Title"),
            content: this.translate.instant("CardNotFound.Content"),
            actions: [{ text: "OK", primary: true, isYes: true }],
            width: 600,
            cssClass: "cardnotfound-dialog",
        });
    }

    ngOnInit() {
        this.init();
    }

    async init() {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.cardModalService.currentDashboard.set(currentDashboard);
        this.cardModalService.useiFirmUserId.set(this.usersService.useiFirmUserId(currentDashboard));
        this.cardModalService.cardType.set(currentDashboard.ApplicationType);
        this.cardModalService.isiFirmTaxDashboard.set(isiFirmTaxDashboard(currentDashboard));
        this.cardModalService.isJobsBillingDashboard.set(isJobsBillingDashboard(currentDashboard));
        this.cardModalService.canEditAssignee.set(
            this.authService.currentUser.can_edit_assignee === "True" ||
                (!this.cardModalService.isiFirmTaxDashboard() && !this.cardModalService.isJobsBillingDashboard()),
        );
        this.cardModalService.canContactsViewAddEdit.set(
            this.authService.currentUser.can_contacts_view_add_edit === "True" ||
                (!this.cardModalService.isiFirmTaxDashboard() && !this.cardModalService.isJobsBillingDashboard()),
        );

        if (!this.helpersService.stepsMerged) {
            await this.helpersService.mergeSteps();
        }
        if (this.columnDataLayoutService.cardsAreLoaded) {
            await this.loadCardData();
        } else {
            // only happens if user accesses card modal via direct url
            return false;
        }
    }

    async loadCardData() {
        this.cardModalService.cardId.set(decodeURIComponent(this.route.snapshot.params["cardId"]));

        const isJobsBilling = this.cardModalService.isJobsBillingDashboard();

        if (this.cardModalService.isCardChanged() && !isJobsBilling) {
            this.fromType = this.cardData.return.type;
        }

        try {
            this.cardData = UtilsService.recursiveDeepCopy(await this.cardDisplayService.getFullCard(this.cardModalService.cardId()));
            if (this.cardData?.members || (isJobsBilling && this.cardData)) {
                this.initVars(this.cardData);

                if (this.cardModalService.isCardChanged() && !isJobsBilling) {
                    this.addCardChangeAnalyticsData();

                    this.cardModalService.isCardChanged.set(false);
                    this.fromType = "";
                }
            } else {
                this.cardModalService.cardNotFound.set(true);
                if (this.cardData) {
                    this.analyticsService.track("cardData members property was unassigned", {
                        currentDashboard: this.cardModalService.currentDashboard()?.Id,
                        cardId: this.cardModalService.cardId(),
                    });
                }

                // Timeout is to let angular inject kendo-dialog in DOM
                setTimeout(() => {
                    this.openCardNotFoundDialog().subscribe(() => {
                        this.onClose(null);
                    });
                }, 50);
            }
        } catch (e) {
            if (e.status == 404) {
                this.cardModalService.cardNotFound.set(true);
                // Timeout is to let angular inject kendo-dialog in DOM
                setTimeout(() => {
                    this.openCardNotFoundDialog().subscribe(() => {
                        this.onClose(null);
                    });
                }, 50);
            }
        }
    }

    initVars(cardData) {
        const currentDashboard = this.cardModalService.currentDashboard();
        this.clientCodeToUse = currentDashboard?.ClientCode;

        this.cardData.contactInformation.phone = this.helpersService.formatPhoneNumber(cardData.contactInformation.phone);
        this.cardData.contactInformation.cellPhone = this.helpersService.formatPhoneNumber(cardData.contactInformation.cellPhone);
        this.cardModalService.currentCard.set(cardData);
    }

    openDeleteCardConfirmation() {
        const memberCount = (this.cardModalService.currentCard() as IBaseT1Card)?.members?.length ?? 0;

        let deleteContent: string;
        if (memberCount > 0) {
            deleteContent = this.translate.instant("CardModal.DeleteACard.TextMultiple");
        } else if (memberCount === 0) {
            deleteContent = this.translate.instant("CardModal.DeleteACard.Text");
        } else {
            deleteContent = "";
        }

        this.openDialogConfirmation({
            title: this.translate.instant("CardModal.DeleteCard"),
            content: deleteContent,
            actions: [
                { text: this.translate.instant("General.Delete"), primary: true, isYes: true },
                { text: this.translate.instant("General.Cancel") },
            ],
            width: 600,
            cssClass: "card-delete-dialog",
        }).subscribe((result) => {
            if (result) this.deleteCard();
            this.cardModalService.deleteCard.set(false);
        });
    }

    openDialogConfirmation(settings, retries = 0) {
        return new Observable((observer) => {
            if (this.cardModalService.showModal() || this.cardModalService.cardNotFound()) {
                this.dialogService
                    .open(settings)
                    .result.pipe(
                        map((result: any) => {
                            return result?.isYes;
                        }),
                    )
                    .subscribe((result) => {
                        observer.next(result);
                        observer.complete();
                    });
            } else if (retries < 5) {
                setTimeout(() => {
                    this.openDialogConfirmation(settings, retries + 1).subscribe((result) => {
                        observer.next(result);
                        observer.complete();
                    });
                }, 2000);
            } else {
                observer.error();
                observer.complete();
            }
        });
    }

    openPendingChangesDialogConfirmation(): Observable<number> {
        const dialog = this.dialogService.open({
            title: this.translate.instant("PendingChanges.Title"),
            content: this.translate.instant("PendingChanges.Content"),
            actions: [
                { text: this.translate.instant("Actions.Save"), primary: true, choice: 1 }, // Save and proceed
                { text: this.translate.instant("General.Discard"), choice: 2 }, // Proceed without save
            ],
            width: 600,
            cssClass: "card-pending-changes-dialog",
        });

        setTimeout(() => {
            const hostEl = dialog.dialog.location.nativeElement as HTMLElement;
            const closeBtn = hostEl.querySelector(
                ".k-window-titlebar .k-window-titlebar-actions .k-button, .k-dialog-titlebar .k-dialog-titlebar-actions .k-button",
            );
            if (closeBtn) {
                closeBtn.removeAttribute("title");
                closeBtn.removeAttribute("aria-label");
            }
        });

        return dialog.result.pipe(
            map((result: any) => {
                return result.choice;
            }),
        );
    }

    deleteCard() {
        this.analyticsService.track("Deleted a card");
        const currentCard = this.cardModalService.currentCard();
        if (currentCard) {
            const promises = [];
            promises.push(
                new Promise<string>((resolve) => {
                    this.cardService.deleteCard(currentCard.cardId).subscribe(
                        () => {
                            resolve(currentCard.cardId);
                        },
                        () => {
                            this.responseErrorHandler.showPopup({
                                typeLayout: "ERROR",
                                messageCode: "429TooManyRequests",
                            });
                        },
                    );
                }),
            );
            // Delete card members also
            if ((isTaxprepT1Card(currentCard) || isIFirmTaxprepT1Card(currentCard)) && currentCard.members) {
                this.cardDisplayService.removeCardById(currentCard.cardId);
                const members = this.cardModalService.members();
                currentCard.members.forEach((member) => {
                    const memberData = members[member.cardId];
                    if (memberData) {
                        this.cardDisplayService.removeCardById(member.cardId);
                    }
                    promises.push(
                        new Promise<string>((resolve) => {
                            this.cardService.deleteCard(member.cardId).subscribe(
                                () => {
                                    resolve(member.cardId);
                                },
                                () => {
                                    this.responseErrorHandler.showPopup({
                                        typeLayout: "ERROR",
                                        messageCode: "429TooManyRequests",
                                    });
                                },
                            );
                        }),
                    );
                });
            }

            Promise.all(promises).then(async (values) => {
                if (this.cardModalService.isiFirmTaxDashboard() && values.length !== 0)
                    await this.ifirmTaxCardsService.deleteCards(currentCard.dashboardId, values);
                else if (this.cardModalService.cardType() === ApplicationType.JobsBilling) {
                    await this.jobsAndBillingCommonService.deleteCard(currentCard.cardId);
                }
            });
        }
        this.router.navigate(["../"], { relativeTo: this.route });
    }

    async handleCardPositionInCol(cardId: string, stepId: string) {
        if (cardId && stepId) {
            this.cardPositionDataInColumn.cardId = cardId;
            this.cardPositionDataInColumn.stepId = this.cardViewService.currentViewType === "all" ? "all_view" : stepId;
            const isDetailViewOnLandingPage =
                this.cardViewService.currentViewDisplayMode == "details" &&
                this.columnDataLayoutService.loadedColumns &&
                UnassignedUserId in this.columnDataLayoutService.loadedColumns &&
                this.columnDataLayoutService.loadedColumns[UnassignedUserId] &&
                Object.hasOwn(this.columnDataLayoutService.loadedColumns[UnassignedUserId] as object, cardId);

            let cards = [];
            const cardModel = await this.cardDisplayService.getModel();

            if (
                this.cardViewService.currentViewDisplayMode == "List" &&
                this.cardViewService.currentViewType === "notStarted" &&
                this.cardViewService.selectedStepId === "all_steps_not_started"
            ) {
                cards = cardModel
                    .filter((it) => it.state === "notStarted")
                    .reverse()
                    .flatMap((model) => model.cards);
            } else if (
                this.cardViewService.currentViewDisplayMode == "List" &&
                this.cardViewService.currentViewType === "completed" &&
                this.cardViewService.selectedStepId === "all_steps_completed"
            ) {
                cards = cardModel
                    .filter((it) => it.state === "completed")
                    .reverse()
                    .flatMap((model) => model.cards);
            } else {
                cards = isDetailViewOnLandingPage
                    ? cardModel.filter((model) => !model.stepId).flatMap((model) => model.cards)
                    : cardModel.filter((model) => model.stepId === this.cardPositionDataInColumn.stepId).flatMap((model) => model.cards);
            }

            if (this.cardViewService.currentViewType === "all") {
                cards = cardModel.flatMap((model) => model.cards);
            }
            cards = this.filtersService.filterCards(cards);
            this.filtersService.sortCards(cards);

            if (cards.length > 0) {
                this.cardPositionDataInColumn.cardIds = cards.map((card) => card.cardId);
                this.cardPositionDataInColumn.cardIds.forEach((currentCardId, i) => {
                    if (currentCardId === cardId) {
                        this.cardPositionDataInColumn.currentIndex = i;
                        this.cardPositionDataInColumn.showLeftArrow = i > 0;
                        this.cardPositionDataInColumn.showRightArrow =
                            this.cardPositionDataInColumn.cardIds.length > 0 && i < this.cardPositionDataInColumn.cardIds.length - 1;
                    }
                });
            }
        }
    }

    handleArrowClick(type) {
        if (!this.cardModalService.savingChanges()) {
            let index = this.cardPositionDataInColumn.currentIndex;
            if (type === "next") {
                index++;
                this.analyticsService.track("Going to the next card");
            } else {
                index--;
                this.analyticsService.track("Going to the previous card");
            }
            const nextCardId = this.cardPositionDataInColumn.cardIds[index];
            this.handlePendingChanges(nextCardId);
        }
    }

    onClose(e) {
        if (e) {
            e.preventDefault();
        }
        this.handlePendingChanges();
    }

    handlePendingChanges(cardId?: string) {
        // A save already in progress owns the current card; do not switch out from under it. The
        // switch must be gated behind the confirmation prompt AND the save result (DASH-21397).
        if (this.cardModalService.savingChanges()) {
            return;
        }
        if (this.formChanged() && !this.cardDeleted) {
            this.openPendingChangesDialogConfirmation().subscribe(async (result) => {
                if (result <= 2) {
                    if (result === 1) {
                        this.cardModalService.showLoader.set(true);
                        await this.saveChanges(false, () => {
                            this.cardModalService.cardModalFormGroup().markAsPristine();
                            this.cardModalService.cardModalFormGroup().markAsUntouched();

                            Object.values(this.cardModalService.cardModalFormGroup().controls).forEach((control) => {
                                control.markAsPristine();
                                control.markAsUntouched();
                            });

                            if (cardId) {
                                this.router.navigate(["../" + cardId], { relativeTo: this.route });
                            } else {
                                this.router.navigate(["../"], { relativeTo: this.route });
                            }
                        });
                    } else if (cardId) {
                        this.router.navigate(["../" + cardId], { relativeTo: this.route });
                    } else {
                        this.router.navigate(["../"], { relativeTo: this.route });
                    }
                }
            });
        } else if (cardId) {
            this.router.navigate(["../" + cardId], { relativeTo: this.route });
        } else {
            this.router.navigate(["../"], { relativeTo: this.route });
        }
    }

    formChanged() {
        return this.cardModalService.cardModalFormGroup() ? this.cardModalService.cardModalFormGroup().dirty : false;
    }

    async saveChanges(isCloseOnSave: boolean, callback?: () => void) {
        this.cardModalService.cardIdSaving.set([...this.cardModalService.cardIdSaving(), this.cardModalService.currentCard()?.cardId]);
        if (this.cardModalService.saveChangeEvent) {
            await this.cardModalService.saveChangeEvent(isCloseOnSave, callback);
        }
    }

    private async getDocument(): Promise<number | undefined> {
        const documentId = !this.dashboardsService.isJobsBilling()
            ? (this.cardModalService.currentCard() as IFirmTaxprepT1Card)?.documentId
            : (this.cardModalService.currentCard() as JobsBillingCard)?.return?.documentId;
        const productType = !this.dashboardsService.isJobsBilling()
            ? "t1"
            : String((this.cardModalService.currentCard() as JobsBillingCard)?.return?.productType).substring(0, 2);
        return await this.iFirmPortalService.getiFirmDocument(productType, documentId);
    }

    async handleOpenInPortal() {
        const documentId = (
            !this.dashboardsService.isJobsBilling()
                ? (this.cardModalService.currentCard() as IFirmTaxprepT1Card)?.documentId
                : (this.cardModalService.currentCard() as JobsBillingCard)?.return?.documentId
        ) as string;
        const returnId = (
            !this.dashboardsService.isJobsBilling()
                ? (this.cardModalService.currentCard() as IFirmTaxprepT1Card)?.return?.id
                : (this.cardModalService.currentCard() as JobsBillingCard)?.return?.returnId
        ) as string;
        const mainTaxPayer = !this.dashboardsService.isJobsBilling()
            ? this.cardModalService.members().find((t) => t.memberType === "Taxpayer")
            : undefined;
        const mainTaxpayerContactId = (
            !this.dashboardsService.isJobsBilling()
                ? mainTaxPayer?.contactId
                : this.cardModalService.currentCard()?.contactInformation?.iFirmContactId
        ) as string;
        const activeTaxPayerContactId = (
            !this.dashboardsService.isJobsBilling()
                ? this.cardModalService.selectedTaxpayer()?.contactId
                : this.cardModalService.currentCard()?.contactInformation?.iFirmContactId
        ) as string;
        const activeTaxPayerMemberType = (
            !this.dashboardsService.isJobsBilling() ? this.cardModalService.selectedTaxpayer()?.memberType : "Taxpayer"
        ) as string;
        const portalId = await this.iFirmPortalService.getiFirmPortalId(
            documentId,
            activeTaxPayerContactId,
            activeTaxPayerMemberType,
            mainTaxpayerContactId,
        );
        if (portalId) {
            let taxYear;
            if (this.dashboardsService.isJobsBilling()) {
                taxYear = await this.getDocument();
            } else taxYear = this.cardModalService.currentDashboard()?.TaxYear;

            this.openInPortal(portalId, taxYear);
            return;
        }

        let title = "";
        let content = "";
        if (this.authService.currentUser?.can_client_portal_admin === "True") {
            title = this.translate.instant("PortalMessages.NoPortalId.Title");
            content = this.translate
                .instant("PortalMessages.NoPortalId.Content")
                .replace("{0}", this.iframeMessagesService.messagesOrigin)
                .replace("{1}", activeTaxPayerContactId);
        } else {
            title = this.translate.instant("PortalMessages.NoAccess.Title");
            content = this.translate.instant("PortalMessages.NoAccess.Content");
        }

        const dialog = this.dialogService.open({
            title: title,
            content: HtmlDialogContentComponent,
            actions: [{ text: this.translate.instant("Actions.Close"), primary: true, choice: 1 }],
            width: 600,
            cssClass: "card-pending-changes-dialog",
        });
        dialog.content.instance.htmlContent = content;
        dialog.result.pipe();
    }

    openInPortal(portalId: number, taxYear: number | null | undefined) {
        const card = this.cardModalService.currentCard();
        this.analyticsService.track("Click on the Portal link in the card", getAnalyticsExtrasFromCard(card));
        window.open(
            `${this.iframeMessagesService.messagesOrigin}/#/crm/client_portal_documents?clientId=${portalId}&path=clientportaldocs%2F${portalId}%2F${taxYear}`,
            "_blank",
        );
    }

    handleOpenInContact() {
        const card = this.cardModalService.currentCard();
        let extras = getAnalyticsExtrasFromCard(card, {
            // TODO clickOnTheClientCode,
        });
        this.analyticsService.track("Click on the Contact link in the card", extras);
        window.open(
            `${this.iframeMessagesService.messagesOrigin}/#/crm/client_manager?mode=edit&id=${this.cardModalService.currentCard()?.contactInformation?.iFirmContactId}&tab=summaryTab`,
            "_blank",
        );
    }

    ngOnDestroy() {
        this.cardsLoadedSubscription.unsubscribe();
        this.routeParamsSubscription?.unsubscribe();
    }

    saveSidebarSettingsToLocalStorage(value) {
        LocalStorageService.setItem("OpenCard_Sidebar_Collapsed_State_" + this.helpersService.getCurrentUser().sub, value);
    }

    setWeekStartDay() {
        if (this.helpersService.getLang() == "fr-CA") {
            const frData: any = localeData(this.helpersService.getLang());
            frData.firstDay = 0;
            setData(frData);
            this.intlService.notify();
        }
    }

    addCardChangeAnalyticsData() {
        this.analyticsService.track("Changing member in card modal", {
            ...getAnalyticsExtrasFromCard(this.cardData),
            fromType: this.fromType,
            toType: this.cardData.return.type,
        });
    }

    @HostListener("document:visibilitychange", [])
    onVisibilityChange() {
        setTimeout(() => {
            globalThis.getSelection().removeAllRanges();
        }, 0);
    }
}
